// src/contexts/NotificationsContext.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { GlobalToast } from "../components/Notifications/GlobalToast";

/* ======================================================
 🧠 MODELO PROFISSIONAL DE NOTIFICAÇÃO (SaaS)
====================================================== */

const defaultNotificationsContext = {
  notifications: [],
  unreadCount: 0,

  // ações base
  markAsRead: () => {},
  markAllAsRead: () => {},

  // criação genérica
  addNotification: () => {},
  removeNotification: () => {},
  clearAll: () => {},

  // helpers de negócio (nível SaaS)
  notifySystem: () => {},
  notifyAgenda: () => {},
  notifyClient: () => {},
  notifyProperty: () => {},
  notifyFinance: () => {},

  // 🆕 TOAST
  showToast: () => {},
  closeToast: () => {},
  toast: null,
};

const NotificationsContext = createContext(defaultNotificationsContext);

/* ======================================================
 🧠 UTILIDADES
====================================================== */

const STORAGE_PREFIX = "imoveispro.notifications";
const DAYS_30_MS = 30 * 24 * 60 * 60 * 1000;

// ✅ uuid seguro (fallback)
function safeUUID() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `n_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ⏱️ Tempo humano (UX premium)
function humanTime(timestamp) {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  return `há ${days} dias`;
}

// 🧹 Remove notificações antigas
function cleanupOld(notifs) {
  const now = Date.now();
  return (notifs || []).filter((n) => {
    const createdAt = Number(n?.createdAt || 0);
    if (!createdAt) return false;
    return now - createdAt <= DAYS_30_MS;
  });
}

// 💾 key por usuário (preparado pra multi-user no futuro)
function storageKeyForUser(userId) {
  const safe = userId ? String(userId) : "anon";
  return `${STORAGE_PREFIX}.${safe}`;
}

function safeParseJSON(str, fallback) {
  try {
    const val = JSON.parse(str);
    return val ?? fallback;
  } catch (_) {
    return fallback;
  }
}

/* ======================================================
 🔔 PROVIDER
====================================================== */

export function NotificationsProvider({ children, userId }) {
  // 🆕 ESTADO DO TOAST
  const [toast, setToast] = useState(null);

  // 📱 DETECCIÓN DE MÓVIL
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ✅ carrega do localStorage no primeiro render
  const [notifications, setNotifications] = useState(() => {
    const key = storageKeyForUser(userId);
    const raw = localStorage.getItem(key);
    const stored = raw ? safeParseJSON(raw, []) : [];

    const cleaned = cleanupOld(stored);

    // Se não houver nenhuma, cria a welcome (uma vez)
    if (!cleaned.length) {
      return [
        {
          id: "welcome",
          type: "success",
          category: "system",
          title: "Bem-vindo 👋",
          message: "Seu perfil está pronto para uso.",
          actionUrl: "/settings/perfil",
          read: false,
          createdAt: Date.now(),
        },
      ];
    }

    return cleaned;
  });

  /* =============================
     PERSISTÊNCIA (sempre que mudar)
  ============================= */

  useEffect(() => {
    const key = storageKeyForUser(userId);
    const cleaned = cleanupOld(notifications);

    // evita salvar lixo se houver limpeza
    if (cleaned.length !== notifications.length) {
      setNotifications(cleaned);
      return;
    }

    try {
      localStorage.setItem(key, JSON.stringify(cleaned));
    } catch (_) {
      // se localStorage falhar, só não quebra a app
    }
  }, [notifications, userId]);

  /* =============================
     LIMPEZA AUTOMÁTICA (1x por dia)
  ============================= */

  useEffect(() => {
    const interval = setInterval(() => {
      setNotifications((prev) => cleanupOld(prev));
    }, 24 * 60 * 60 * 1000); // 1 dia

    return () => clearInterval(interval);
  }, []);

  /* =============================
     DERIVADOS
  ============================= */

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  /* =============================
     AÇÕES BASE
  ============================= */

  const markAsRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // ✅ addNotification com "pulse" para UI (animar só unread)
  const addNotification = useCallback((notification) => {
    setNotifications((prev) => {
      const item = {
        id: safeUUID(),
        read: false,
        createdAt: Date.now(),
        pulse: true, // anima só as não lidas
        ...notification,
      };

      return [item, ...prev];
    });
  }, []);

  /* =============================
     🆕 TOAST FUNCTIONS - CORREGIDO CON DETECCIÓN MÓVIL
  ============================= */

  const showToast = useCallback((message, type = 'info', options = {}) => {
    const id = Date.now();
    
    // Detectar si es móvil para ajustar posición y duración
    const mobile = isMobile;
    
    const toastConfig = {
      id,
      message,
      type,
      duration: mobile ? (options.duration || 3500) : (options.duration || 4000), // Un poco más corto en móvil
      title: options.title,
      position: mobile ? 'top-center' : (options.position || 'top-right'),
      animation: options.animation || 'slideIn',
      actions: options.actions,
      metadata: options.metadata
    };
    
    setToast(toastConfig);
  }, [isMobile]);

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  /* =============================
     HELPERS DE NEGÓCIO (MELHORADOS COM TOAST)
  ============================= */

  const notifySystem = useCallback(
    ({ title, message, actionUrl }) => {
      addNotification({
        type: "info",
        category: "system",
        title,
        message,
        actionUrl,
      });
      
      // Também mostra toast
      showToast(message, 'info', { title });
    },
    [addNotification, showToast]
  );

  const notifyAgenda = useCallback(
    ({ title, message, actionUrl }) => {
      addNotification({
        type: "warning",
        category: "agenda",
        title,
        message,
        actionUrl,
      });
      
      showToast(message, 'warning', { title });
    },
    [addNotification, showToast]
  );

  const notifyClient = useCallback(
    ({ title, message, actionUrl }) => {
      addNotification({
        type: "info",
        category: "client",
        title,
        message,
        actionUrl,
      });
      
      showToast(message, 'info', { title });
    },
    [addNotification, showToast]
  );

  const notifyProperty = useCallback(
    ({ title, message, actionUrl }) => {
      addNotification({
        type: "success",
        category: "property",
        title,
        message,
        actionUrl,
      });
      
      showToast(message, 'success', { title });
    },
    [addNotification, showToast]
  );

  const notifyFinance = useCallback(
    ({ title, message, actionUrl }) => {
      addNotification({
        type: "danger",
        category: "finance",
        title,
        message,
        actionUrl,
      });
      
      showToast(message, 'error', { title });
    },
    [addNotification, showToast]
  );

  /* =============================
     🎯 EVENT LISTENERS PARA AUTH - INTEGRADO
  ============================= */
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleAuthNotification = (event) => {
      const { type, message, title, duration, metadata } = event.detail;
      showToast(message, type, { title, duration, metadata });
    };
    
    const handleAuthAddNotification = (event) => {
      addNotification(event.detail);
    };
    
    window.addEventListener('auth:notification', handleAuthNotification);
    window.addEventListener('auth:addNotification', handleAuthAddNotification);
    
    return () => {
      window.removeEventListener('auth:notification', handleAuthNotification);
      window.removeEventListener('auth:addNotification', handleAuthAddNotification);
    };
  }, [showToast, addNotification]);

  /* =============================
     CONTEXTO FINAL
  ============================= */

  const value = useMemo(
    () => ({
      notifications: notifications.map((n) => ({
        ...n,
        timeLabel: humanTime(n.createdAt),
      })),
      unreadCount,

      markAsRead,
      markAllAsRead,
      addNotification,
      removeNotification,
      clearAll,

      notifySystem,
      notifyAgenda,
      notifyClient,
      notifyProperty,
      notifyFinance,

      // 🆕 TOAST
      showToast,
      closeToast,
      toast,
      
      // 📱 Helper para detectar móvil en componentes
      isMobile,
    }),
    [
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      addNotification,
      removeNotification,
      clearAll,
      notifySystem,
      notifyAgenda,
      notifyClient,
      notifyProperty,
      notifyFinance,
      showToast,
      closeToast,
      toast,
      isMobile,
    ]
  );

  // ✅ FIX: Garantir que children seja sempre um elemento consistente
  const renderContent = useMemo(() => {
    return (
      <>
        <GlobalToast toast={toast} onClose={closeToast} />
        {children}
      </>
    );
  }, [toast, closeToast, children]);

  return (
    <NotificationsContext.Provider value={value}>
      {renderContent}
    </NotificationsContext.Provider>
  );
}

/* ======================================================
 🔒 HOOK
====================================================== */

export function useNotifications() {
  const context = useContext(NotificationsContext);
  
  // Verificación de seguridad para desarrollo
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationsProvider');
  }
  
  return context;
}

/* ======================================================
 🎯 EXPORTACIONES ADICIONAIS (OPCIONAL)
====================================================== */

// Helpers para criar toasts rapidamente
export const toastHelpers = {
  success: (message, options) => ({ 
    type: 'success', 
    message, 
    ...options 
  }),
  error: (message, options) => ({ 
    type: 'error', 
    message, 
    ...options 
  }),
  warning: (message, options) => ({ 
    type: 'warning', 
    message, 
    ...options 
  }),
  info: (message, options) => ({ 
    type: 'info', 
    message, 
    ...options 
  }),
  achievement: (message, options) => ({ 
    type: 'achievement', 
    message, 
    ...options 
  })
};

// ======================================================
// 🎯 HOOK DE CONVENIENCIA PARA EVENTOS DE AUTH (OPCIONAL)
// ======================================================
export function useAuthNotifications() {
  const { showToast, addNotification } = useNotifications();
  
  return {
    showToast,
    addNotification,
    emitTest: (type, message) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:notification', {
          detail: { type, message }
        }));
      }
    }
  };
}